import { isProxy, isRef, toRaw, unref } from 'vue';

/**
 * Converte um valor reativo do Vue em estrutura que o `structuredClone` aceita.
 *
 * ## Por que isto existe
 *
 * `IndexedDB.put()` clona o que recebe com o algoritmo de structured clone, que
 * **recusa Proxy**. E `toRaw()` desembrulha só UM nível: num objeto reativo, cada
 * campo aninhado é embrulhado de novo na leitura, então `{ ...mensagem }` copia o
 * topo e preserva os proxies de dentro.
 *
 * O caso real: `Message.reactions` é um `Map` — e o Vue **proxia** `Map` (ao
 * contrário de `File`/`Blob`, que ele deixa passar intactos). Gravar uma mensagem
 * pendente estourava
 * `DOMException: Proxy object could not be cloned` no `put`, e como o chamador usa
 * `void savePendingMessageToDB(...)`, aparecia como `Uncaught (in promise)`.
 *
 * ## O que preserva e o que descarta
 *
 * `File`, `Blob`, `ArrayBuffer`, `Date` e `RegExp` passam por referência: já são
 * clonáveis e recriá-los perderia conteúdo. `Map`/`Set`/array/objeto são
 * reconstruídos com os membros também desembrulhados.
 *
 * **Função é descartada**, não propagada: `structuredClone` a recusa, e uma chave
 * com função num cache de mensagem é sempre acidente de reatividade — melhor perder
 * a chave do que perder a gravação inteira.
 */
const PROFUNDIDADE_MAXIMA = 20;

/** Tipos que o structured clone já aceita e que NÃO devem ser reconstruídos. */
function ehOpaco(v: object): boolean {
    return v instanceof Blob
        || v instanceof ArrayBuffer
        || v instanceof Date
        || v instanceof RegExp
        || (typeof ImageBitmap !== 'undefined' && v instanceof ImageBitmap)
        || ArrayBuffer.isView(v);
}

export function paraClonavel<T>(valor: T, profundidade = 0): T {
    if (profundidade > PROFUNDIDADE_MAXIMA) return undefined as unknown as T;

    const cru = isRef(valor) ? unref(valor) : isProxy(valor) ? toRaw(valor) : valor;

    if (cru === null || typeof cru !== 'object') return cru as T;
    if (ehOpaco(cru as object)) return cru as T;

    if (Array.isArray(cru)) {
        return cru.map((v) => paraClonavel(v, profundidade + 1)) as unknown as T;
    }
    if (cru instanceof Map) {
        const saida = new Map();
        for (const [k, v] of cru) {
            saida.set(paraClonavel(k, profundidade + 1), paraClonavel(v, profundidade + 1));
        }
        return saida as unknown as T;
    }
    if (cru instanceof Set) {
        const saida = new Set();
        for (const v of cru) saida.add(paraClonavel(v, profundidade + 1));
        return saida as unknown as T;
    }

    const saida: Record<string, unknown> = {};
    for (const [chave, v] of Object.entries(cru as Record<string, unknown>)) {
        if (typeof v === 'function') continue;
        saida[chave] = paraClonavel(v, profundidade + 1);
    }
    return saida as T;
}
